import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useMutation, useQuery } from "@apollo/client/react";
import { PersonalSharingRecipientsDocument, SetGroupSharingEligibilityDocument } from "@/graphql/generated/operations";
import { Switch } from "@/components/design/Switch";
import { Tooltip } from "@/components/common/Tooltip";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError } from "@/lib/localized-error";
import { userErrorMessage } from "@/lib/user-error";

export function GroupSharingEligibility({ groupId }: { groupId: string }) {
  const intl = useIntl();
  const query = useQuery(PersonalSharingRecipientsDocument, { fetchPolicy: "network-only" });
  const [change] = useMutation(SetGroupSharingEligibilityDocument);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<boolean | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const stored = query.data?.personalSharingRecipients.some((group) => group.id === groupId) ?? false;
  const checked = draft ?? stored;
  const dirty = !!query.data && checked !== stored;
  usePendingScope(`group-sharing-${groupId}`, { dirty, save, discard: () => { setDraft(null); setError(null); } });
  function save(): Promise<void> {
    if (inFlight.current) return inFlight.current;
    const pending = update(checked).finally(() => { inFlight.current = null; });
    inFlight.current = pending;
    return pending;
  }
  const [error, setError] = useState<string | null>(null);
  async function update(eligible: boolean) {
    if (!dirty) return;
    setLoading(true);
    try {
      await change({ variables: { input: { groupId, eligible } } });
      await query.refetch();
      setDraft(null);
      setError(null);
    } catch (cause) {
      const message = userErrorMessage(intl, cause);
      setError(message);
      throw new LocalizedError(message);
    } finally { setLoading(false); }
  }
  return <div className="space-y-2">
    <Tooltip content={intl.formatMessage({ id: "admin.groups.sharingHint" })}>
      <span className="inline-flex max-w-full items-center gap-3">
        <span className="text-smycles-body text-ink">{intl.formatMessage({ id: "admin.groups.sharingEligible" })}</span>
        <Switch id="group-sharing-eligible" checked={checked}
          disabled={loading || query.loading || !query.data || !!query.error} onChange={setDraft}
          ariaLabel={intl.formatMessage({ id: "admin.groups.sharingEligible" })} />
      </span>
    </Tooltip>
    <p className="max-w-3xl text-smycles-meta text-ink-muted">{intl.formatMessage({ id: "admin.groups.sharingSelectionHint" }, {
      on: (text) => <span className={checked ? "mb-2 block font-semibold text-ink" : "mb-2 block"}>{text}</span>,
      off: (text) => <span className={!checked ? "mb-2 block font-semibold text-ink" : "mb-2 block"}>{text}</span>,
    })}</p>
    {(error || query.error) && <p role="alert" className="text-smycles-body text-ink">
      {error ?? userErrorMessage(intl, query.error)}
    </p>}
  </div>;
}
